"use client";

/**
 * Authorization Provider - LEGACY WRAPPER
 * 
 * This provider now wraps the unified auth system to provide backward
 * compatibility while components are being migrated.
 * 
 * NEW CODE SHOULD USE: import { useAuth } from '@/hooks/useAuth'
 * LEGACY CODE: Can continue using useAuthorization() during migration
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { usePathname } from 'next/navigation';
import { calculatePermissions } from './permissions';
import { debug } from '../debug';
import {
    AuthorizationContextValue,
    UserPermissions,
    TrialRecord,
    AppRecord
} from './types';

const AuthorizationContext = createContext<AuthorizationContextValue | null>(null);

const POLLING_INTERVAL = 60000; // 60 seconds fallback polling
const REALTIME_RETRY_DELAY = 5000; // 5 seconds before retrying realtime

interface AuthorizationProviderProps {
    children: React.ReactNode;
}

export function AuthorizationProvider({ children }: AuthorizationProviderProps) {
    const { user, tier, trial, isAuthenticated, authData, isLoading, refresh } = useAuth();
    const pathname = usePathname();
    const [permissions, setPermissions] = useState<UserPermissions | null>(null);
    const [apps, setApps] = useState<AppRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
    const lastLoggedRef = useRef<number>(0);

    /**
     * Calculate permissions from unified auth data
     */
    const refreshPermissions = useCallback(async () => {
        try {
            if (!authData || !user) {
                setPermissions(null);
                setLoading(false);
                return;
            }

            // Convert unified auth data to legacy permission format
            const mockTrials: TrialRecord[] = (authData.allTrials || []).map(t => ({
                id: (t as any).id || `${t.app_slug}-${user.id}`,
                user: user.id,
                app_slug: t.app_slug,
                started_at: t.start_date || t.started_at,
                expires_at: t.end_date || t.expires_at,
                active: t.status === 'active' || t.active === true,
                created: t.start_date || t.started_at,
                updated: t.start_date || t.started_at
            }));

            // Fetch app records if needed
            let currentApps = apps;
            if (apps.length === 0) {
                try {
                    const res = await fetch('/api/apps');
                    if (res.ok) {
                        const data = await res.json();
                        currentApps = data.apps || [];
                        setApps(currentApps);
                    }
                } catch (e) {
                    console.error('Failed to fetch apps for permissions:', e);
                }
            }

            // Calculate permissions using existing logic
            const calculatedPermissions = await calculatePermissions(user as any, mockTrials, currentApps);

            setPermissions(calculatedPermissions);
            setLastUpdated(new Date());
            setError(null);
        } catch (err) {
            console.error('Failed to calculate permissions:', err);
            setError(err instanceof Error ? err : new Error('Permission calculation failed'));
        } finally {
            setLoading(false);
        }
    }, [authData, user]);

    // Update permissions when auth data changes
    useEffect(() => {
        setLoading(isLoading);
        if (!isLoading) {
            refreshPermissions();
        }
    }, [authData, isLoading, refreshPermissions]);

    // Session-based logging logic
    const logAuthReport = useCallback(() => {
        if (!permissions || !user) return;

        const now = Date.now();
        lastLoggedRef.current = now;

        if (process.env.NODE_ENV !== 'development') return; // Only log in dev

        /*
        console.group(`🔐 [Auth Session] ${new Date().toLocaleTimeString()}`);
        console.log(`User: ${user.email} | Tier: ${permissions.effectiveTier}`);

        const report: Record<string, string> = {};
        Object.values(permissions.apps).forEach(app => {
            let status = 'Locked';
            if (app.hasAccess) {
                if (app.accessReason === 'trial') {
                    status = `Trial (${app.trial?.daysLeft} days left)`;
                } else if (app.accessReason === 'subscription') {
                    status = `Subscription (Level ${app.minTier}+)`;
                } else if (app.accessReason === 'free') {
                    status = 'Free';
                }
            } else if (app.trial && !app.trial.active) {
                status = 'Trial Expired';
            } else {
                status = `Requires Level ${app.minTier}`;
            }
            report[app.name || app.slug] = status;
        });

        console.table(report);
        console.groupEnd();
        */
    }, [permissions, user]);

    // Periodic logging and refreshing
    useEffect(() => {
        // Log on mount or when permissions change
        logAuthReport();

        // Set up interval for refreshing auth data (every 2 minutes)
        const refreshInterval = setInterval(() => {
            // debug.log('🔄 [AuthProvider] Auto-refreshing authorization data...');
            refresh();
        }, 2 * 60 * 1000);

        // Set up interval for logging report (every 5 minutes)
        const logInterval = setInterval(() => {
            logAuthReport();
        }, 5 * 60 * 1000);

        // Refresh when window gains focus (but not on login/register pages)
        const handleFocus = () => {
            // Skip refresh on auth pages and dashboard to avoid clearing tokens during login/initial load
            if (pathname === '/login' || pathname === '/register' || pathname === '/reset-password' || pathname === '/dashboard') {
                return;
            }
            // debug.log('🔄 [AuthProvider] Window focused, refreshing auth data...');
            refresh();
        };
        window.addEventListener('focus', handleFocus);

        return () => {
            clearInterval(refreshInterval);
            clearInterval(logInterval);
            window.removeEventListener('focus', handleFocus);
        };
    }, [logAuthReport, refresh, pathname]);

    // Context value
    const contextValue: AuthorizationContextValue = {
        permissions,
        loading,
        error,
        lastUpdated,
        realtimeConnected: false, // Defaulting to false as realtime logic is being deprecated
        refresh: refreshPermissions,
        checkAppAccess: (appSlug: string, minTier?: number) => {
            if (!permissions) return false;
            return permissions.canAccessApp(appSlug) || (minTier !== undefined && permissions.effectiveTier >= minTier);
        }
    };

    return (
        <AuthorizationContext.Provider value={contextValue}>
            {children}
        </AuthorizationContext.Provider>
    );
}

/**
 * Hook to use authorization context (LEGACY - use useAuth instead)
 */
export function useAuthorization(): AuthorizationContextValue {
    const context = useContext(AuthorizationContext);

    if (!context) {
        throw new Error('useAuthorization must be used within AuthorizationProvider');
    }

    return context;
}

/**
 * Debug component to display authorization state (dev only)
 */
export function AuthorizationDebug() {
    const auth = useAuthorization();

    if (process.env.NODE_ENV !== 'development') {
        return null;
    }

    return (
        <div className="fixed bottom-4 right-4 bg-black/90 text-white p-4 rounded-lg text-xs font-mono max-w-md z-50">
            <div className="flex justify-between items-center mb-2">
                <strong>Authorization Debug</strong>
                <button
                    onClick={auth.refresh}
                    className="bg-blue-500 px-2 py-1 rounded text-xs"
                >
                    Refresh
                </button>
            </div>
            <div className="space-y-1">
                <div>Status: {auth.loading ? '⏳ Loading' : '✅ Ready'}</div>
                <div>Realtime: {auth.realtimeConnected ? '🟢 Connected' : '🔴 Polling'}</div>
                <div>Tier: {auth.permissions?.effectiveTier ?? 'N/A'}</div>
                <div>Active Trials: {auth.permissions?.activeTrials.length ?? 0}</div>
                {auth.lastUpdated && (
                    <div>Updated: {auth.lastUpdated.toLocaleTimeString()}</div>
                )}
            </div>
        </div>
    );
}
